import { useRef, type FC, type KeyboardEvent, type PointerEvent } from 'react';

interface ChatPanelResizeHandleProps {
  width: number;
  minWidth: number;
  maxWidth: number;
  onWidthChange: (width: number) => void;
  /** Toggles the panel's transition off while dragging. */
  onDraggingChange?: (dragging: boolean) => void;
  label?: string;
}

const KEY_STEP = 24;

const clamp = (value: number, min: number, max: number) =>
  Math.round(Math.min(max, Math.max(min, value)));

/**
 * The grip on the panel's leading edge. Drag it, or focus it and use the
 * arrow keys (Home / End jump to the limits). The panel grows leftwards, so
 * moving the pointer left widens it.
 */
export const ChatPanelResizeHandle: FC<ChatPanelResizeHandleProps> = ({
  width,
  minWidth,
  maxWidth,
  onWidthChange,
  onDraggingChange,
  label = 'Resize panel'
}) => {
  const drag = useRef<{ x: number; width: number } | null>(null);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { x: event.clientX, width };
    onDraggingChange?.(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) {
      return;
    }
    const next = drag.current.width - (event.clientX - drag.current.x);
    onWidthChange(clamp(next, minWidth, maxWidth));
  };

  const handlePointerEnd = () => {
    if (!drag.current) {
      return;
    }
    drag.current = null;
    onDraggingChange?.(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = {
      ArrowLeft: width + KEY_STEP,
      ArrowRight: width - KEY_STEP,
      Home: maxWidth,
      End: minWidth
    }[event.key];

    if (next === undefined) {
      return;
    }
    event.preventDefault();
    onWidthChange(clamp(next, minWidth, maxWidth));
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onKeyDown={handleKeyDown}
      className="group/resize absolute inset-y-0 left-0 z-10 flex w-2.5 cursor-col-resize touch-none items-center justify-center outline-none hover:bg-brand-subtle focus-visible:bg-brand-subtle max-sm:hidden"
    >
      <span className="h-11 w-[3px] rounded-xs bg-border-strong transition-colors duration-150 group-hover/resize:bg-brand-border group-focus-visible/resize:bg-brand" />
    </div>
  );
};
